import React, { useEffect, useState } from 'react';
import { Wallet, Loader2, Save, AlertCircle, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { getDriverPaymentMethods, updateDriverPaymentPrefs } from '../services/api';

const FALLBACK_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'revolut', label: 'Revolut' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'zippay', label: 'ZipPay' },
  { value: 'other', label: 'Other' },
];

/**
 * Driver Profile — "Pay Driver Directly" payment methods.
 * The driver simply declares which direct methods they accept. There is no
 * payment account to connect and no Stripe onboarding.
 */
export default function PaymentPreferencesPanel() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [options, setOptions] = useState(FALLBACK_OPTIONS);
  const [selected, setSelected] = useState([]);
  const [original, setOriginal] = useState('');

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await getDriverPaymentMethods();
        if (!active) return;
        if (data.options?.length) setOptions(data.options);
        const methods = data.direct_payment_methods || ['cash'];
        setSelected(methods);
        setOriginal(methods.slice().sort().join(','));
      } catch (err) {
        toast.error('Failed to load your payment methods');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const toggle = (value) => {
    setSelected((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const dirty = selected.slice().sort().join(',') !== original;

  const handleSave = async () => {
    if (!selected.length) {
      toast.error('Select at least one payment method you accept.');
      return;
    }
    setSaving(true);
    try {
      const { data } = await updateDriverPaymentPrefs({ direct_payment_methods: selected });
      const methods = data.direct_payment_methods || selected;
      setSelected(methods);
      setOriginal(methods.slice().sort().join(','));
      toast.success('Payment methods saved');
      window.dispatchEvent(new Event('jd:profile-updated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to save payment methods');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="rounded-xl border border-slate-200/80 shadow-sm mb-6" data-testid="payment-prefs-panel">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-10 h-10 bg-emerald-50 flex items-center justify-center rounded-md flex-shrink-0">
            <Wallet className="w-5 h-5 text-[#2E7D32]" strokeWidth={1.8} />
          </div>
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-gray-900">Pay Driver Directly</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Your 10% JustDeliver Driver Fee is unchanged. Once the customer has paid JustDeliver's
              online charges, the remaining amount is paid directly to you by the customer. Choose which
              methods you accept — customers see these on your bids.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {options.map((opt) => {
                const on = selected.includes(opt.value);
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => toggle(opt.value)}
                    className={`flex items-center justify-between gap-3 border rounded-md px-4 py-3 text-left transition-colors ${
                      on ? 'border-[#2E7D32] bg-emerald-50/60' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                    data-testid={`payment-method-option-${opt.value}`}
                    aria-pressed={on}
                  >
                    <span className={`text-sm font-medium ${on ? 'text-[#1B5E20]' : 'text-gray-700'}`}>{opt.label}</span>
                    <span className={`w-5 h-5 rounded flex items-center justify-center border ${
                      on ? 'bg-[#2E7D32] border-[#2E7D32]' : 'border-gray-300 bg-white'
                    }`}>
                      {on && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="text-xs text-gray-500">
              You can select more than one. No payment account or provider needs to be connected to JustDeliver —
              you agree the method with the customer.
            </p>

            {!selected.length && (
              <div className="flex items-start gap-1.5 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5"
                   data-testid="payment-prefs-validation-error">
                <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                <span>Please select at least one payment method.</span>
              </div>
            )}

            <div className="flex justify-end pt-1">
              <Button
                onClick={handleSave}
                disabled={!dirty || saving || !selected.length}
                className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-md gap-2"
                data-testid="payment-prefs-save-btn"
              >
                {saving ? (<><Loader2 className="w-4 h-4 animate-spin" /> Saving…</>) : (<><Save className="w-4 h-4" /> Save payment methods</>)}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
